
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, FileText, AlertTriangle } from 'lucide-react';
import { ReferralIdBankStats } from '@/utils/referral/bank/types';

interface ReferralBankStatsProps {
  bankStats: ReferralIdBankStats | null;
  isGenerating: boolean;
  refreshData: () => void;
  onOpenReportModal: () => void;
  handleCheckAndReplenish: () => void;
  handleGenerateMoreIds: (count: number) => void;
}

const ReferralBankStats: React.FC<ReferralBankStatsProps> = ({
  bankStats,
  isGenerating,
  refreshData,
  onOpenReportModal,
  handleCheckAndReplenish,
  handleGenerateMoreIds
}) => {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Referral ID Bank Statistics</CardTitle>
        <CardDescription>
          Overview of the referral ID bank and allocation status
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h3 className="text-gray-500 text-sm font-medium">Total Referral IDs</h3>
            <p className="text-3xl font-bold text-gray-900">{bankStats?.totalIds || 0}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h3 className="text-gray-500 text-sm font-medium">Available IDs</h3>
            <p className="text-3xl font-bold text-green-600">{bankStats?.unassignedIds || 0}</p>
            {bankStats && bankStats.unassignedIds < 50 && (
              <div className="mt-2 flex items-center text-yellow-600">
                <AlertTriangle className="h-4 w-4 mr-1" />
                <span className="text-sm">Running low on IDs</span>
              </div>
            )}
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h3 className="text-gray-500 text-sm font-medium">Assigned IDs</h3>
            <p className="text-3xl font-bold text-blue-600">{bankStats?.assignedIds || 0}</p>
          </div>
        </div>
        
        <div className="mt-6 flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            onClick={refreshData}
            className="flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
          
          <Button 
            variant="outline"
            onClick={onOpenReportModal}
            className="flex items-center"
          >
            <FileText className="h-4 w-4 mr-2" />
            Detailed Assignment Report
          </Button>
        </div>
        
        <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
          <h4 className="text-blue-800 font-medium flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Note for Super Admins
          </h4>
          <p className="text-blue-700 text-sm mt-1">
            Only Master Admins can generate new referral IDs. If you're running low on available IDs, 
            please contact a Master Admin to generate more.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralBankStats;
